import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useInView } from './useInView'
import { ArrowUpRight } from 'lucide-react'
import spottoMark from '../assets/spotto-mark.png'
import { useT } from '../i18n/LanguageContext'
import type { Dictionary } from '../i18n/types'

type ProjectKey = keyof Dictionary['realizacje']['projects']

interface ProjectMeta {
  key: ProjectKey
  href: string
  external: boolean
  /** Trasa aplikacji — idzie przez router, żeby nie przeładowywać strony. */
  internal?: boolean
  logo?: string
  urlLabel?: string
}

/** Kafel bywa linkiem routera, a bywa zwykłym <a> — animacja obsługuje oba. */
const MotionLink = motion.create(Link)

const projectList: ProjectMeta[] = [
  {
    key: 'spotto',
    href: 'https://spotto.pl',
    external: true,
    logo: spottoMark,
    urlLabel: 'spotto.pl',
  },
  {
    // Nie ma jeszcze własnego adresu — decyzja o osobnej stronie produktu
    // wciąż otwarta, więc kafel prowadzi do kontaktu.
    key: 'pluginCommerce',
    href: '#contact',
    external: false,
  },
  {
    // Case study tego wdrożenia jest już napisane — kafel prowadzi do artykułu,
    // który opisuje je w szczegółach, razem z tym, co poszło nie tak.
    key: 'asystent',
    href: '/blog/ai-crm-czy-ai-sprzedawca',
    external: false,
    internal: true,
    urlLabel: 'Przeczytaj, jak działa',
  },
]

export default function Realizacje() {
  const t = useT()
  const { ref, isInView } = useInView()

  return (
    <section
      id="realizacje"
      className="relative pt-8 pb-24 min-h-[88vh] flex flex-col justify-center overflow-hidden bg-white/[0.012]"
    >
      <div className="absolute inset-0 bg-radial-center" />

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <p className="font-mono text-accent-400 text-xs tracking-[0.2em] uppercase mb-4">
            {t.realizacje.eyebrow}
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6">
            {t.realizacje.title}
          </h2>
          <p className="text-lg text-zinc-400 leading-relaxed">
            {t.realizacje.subtitle}
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {projectList.map((proj, i) => {
            const content = t.realizacje.projects[proj.key]
            const Card = proj.internal ? MotionLink : motion.a
            const target = proj.internal
              ? { to: proj.href }
              : {
                  href: proj.href,
                  target: proj.external ? '_blank' : undefined,
                  rel: proj.external ? 'noopener noreferrer' : undefined,
                }
            return (
              <Card
                key={proj.key}
                {...target}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                className="group relative flex flex-col p-8 rounded-2xl bg-white/[0.05] border border-white/[0.08] hover:border-white/[0.16] hover:bg-white/[0.08] transition-all"
              >
                <div className="flex items-start justify-between mb-6">
                  {proj.logo ? (
                    <img
                      src={proj.logo}
                      alt=""
                      aria-hidden="true"
                      className="h-14 w-auto brightness-0 invert opacity-90"
                    />
                  ) : (
                    <p className="font-mono text-brand-400 text-xs tracking-[0.2em] uppercase">
                      {content.category}
                    </p>
                  )}
                  <ArrowUpRight
                    size={18}
                    className="text-zinc-500 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                  />
                </div>
                {proj.logo && (
                  <p className="font-mono text-brand-400 text-xs tracking-[0.2em] uppercase mb-2">
                    {content.category}
                  </p>
                )}
                <h3 className="font-display text-2xl font-bold text-white mb-3 tracking-tight">
                  {content.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed mb-4">
                  {content.description}
                </p>
                {proj.urlLabel && (
                  // mt-auto trzyma odnośnik przy dolnej krawędzi, żeby przy
                  // różnej długości opisów kafle kończyły się równo.
                  <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-brand-400 group-hover:text-white transition-colors">
                    {proj.urlLabel}
                    <ArrowUpRight size={14} />
                  </span>
                )}
              </Card>
            )
          })}
        </div>
      </div>
    </section>
  )
}
